import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Download, Upload, Check, Heart, Sparkles, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AppointmentPortal() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => {
    axios.get(`${API}/public/appointment/${token}`)
      .then((r) => { setData(r.data); setForm(r.data.patient || {}); })
      .catch((e) => setErr(e.response?.data?.detail || t("portal.invalidLink")));
  };
  useEffect(load, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function submit() {
    if (!form.privacy_consent_accepted) {
      toast.error(t("portal.privacyRequired")); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/public/appointment/${token}/submit`, form);
      toast.success(t("portal.dataSaved")); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); }
    finally { setSaving(false); }
  }

  async function uploadSigned(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await axios.post(`${API}/public/appointment/${token}/upload-signed`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("portal.signedUploaded")); load();
    } catch (_e) { toast.error(t("common.error")); }
    finally { setUploading(false); e.target.value = ""; }
  }

  if (err) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6 text-center relative">
    <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
    <div><p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("common.error")}</p><h2 className="font-heading text-3xl font-light">{err}</h2></div>
  </div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] text-stone-500 relative">
    <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
    {t("common.loading")}
  </div>;

  const dt = new Date(data.date);
  const dateStr = dt.toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" });
  const timeStr = dt.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });

  const F = (k, label, type = "text") => (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{label}</label>
      <input type={type} value={form[k] || ""} data-testid={`portal-${k}`}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]" />
    </div>
  );
  const TA = (k, label, ph = "") => (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{label}</label>
      <textarea value={form[k] || ""} placeholder={ph} rows={2} spellCheck autoCorrect="on" autoCapitalize="sentences"
        onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`portal-${k}`}
        className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6] py-10 px-4">
      <Toaster position="top-center" richColors />
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      <div className="max-w-2xl mx-auto fade-in-up space-y-6">
        <div className="text-center bg-white border border-stone-200 rounded-2xl p-8">
          <Heart className="w-8 h-8 mx-auto text-[#D4A373] mb-4" strokeWidth={1.25} />
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-2">{t("portal.thanksEyebrow")}</p>
          <h1 className="font-heading text-3xl md:text-4xl font-light">
            {t("portal.thanksTitle", { name: data.doctor_name || "" })}
          </h1>
          <p className="text-stone-600 mt-4 max-w-md mx-auto">{t("portal.thanksSub")}</p>
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FAF9F6] border border-stone-200 text-sm">
            <Sparkles className="w-3.5 h-3.5 text-[#B07D4F]" /> {data.treatment} · {dateStr} · {timeStr}
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-1">{t("portal.anamnesiEyebrow")}</p>
              <h3 className="font-heading text-2xl font-light">{t("portal.step1")}</h3>
            </div>
            {data.form_submitted && <span className="flex items-center gap-1 text-xs text-[#8FA08B]"><Check className="w-4 h-4" /> {t("portal.saved")}</span>}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {F("full_name", t("patients.fullName"))}
            {F("phone", t("common.phone"))}
            {F("email", t("common.email"), "email")}
            {F("codice_fiscale", t("patients.cf"))}
            {F("date_of_birth", t("patients.dob"), "date")}
          </div>
          {TA("allergies", t("patients.allergies"), t("booking.allergiesPh"))}
          {TA("diseases", t("portal.medicalPathologies"), t("portal.medicalPathologiesPh"))}
          {TA("surgical_history", t("portal.surgicalPathologies"), t("portal.surgicalPathologiesPh"))}
          {TA("medications", t("patients.medications") || t("booking.medications"), t("booking.medicationsPh"))}
        </div>

        {data.consent_template ? (
          <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-5">
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500">{t("portal.consentEyebrow")}</p>
            <h3 className="font-heading text-2xl font-light">{t("portal.step2")} · {data.consent_template.treatment}</h3>
            <p className="text-sm text-stone-600">{t("portal.step2Desc")}</p>
            <a href={`${API}/public/consent-templates/${data.consent_template.id}/download?appt_token=${token}`}
              target="_blank" rel="noreferrer" data-testid="portal-download-consent"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors">
              <Download className="w-4 h-4" /> {t("portal.downloadConsent")}
            </a>
            <div className="pt-4 border-t border-stone-100">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">{t("portal.uploadSigned")}</p>
              <input ref={fileRef} type="file" accept="application/pdf,image/*" onChange={uploadSigned} className="hidden" data-testid="portal-upload-file" />
              <button onClick={() => fileRef.current.click()} disabled={uploading} data-testid="portal-upload-signed"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-stone-300 hover:bg-stone-50">
                <Upload className="w-4 h-4" /> {uploading ? t("patientDetail.uploading") : data.signed_consent_uploaded ? t("portal.replaceFile") : t("portal.uploadSignedBtn")}
              </button>
              {data.signed_consent_uploaded && <p className="text-xs text-[#8FA08B] mt-3 flex items-center gap-1"><Check className="w-3 h-3" /> {t("portal.consentReceived")}</p>}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-2xl p-6 text-sm text-stone-500 text-center">
            {t("portal.noConsent")}
          </div>
        )}

        <div className="bg-white border border-stone-200 rounded-2xl p-6 space-y-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-[#8FA08B] mt-0.5" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="font-medium text-stone-900">{t("portal.privacyTitle")}</div>
              <div className="text-xs text-stone-500 mt-1">{t("portal.privacyDesc")}</div>
            </div>
          </div>
          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl bg-[#FAF9F6] hover:bg-[#8FA08B]/10 transition-colors">
            <input type="checkbox" data-testid="portal-privacy-check"
              checked={!!form.privacy_consent_accepted}
              onChange={(e) => setForm({ ...form, privacy_consent_accepted: e.target.checked })}
              className="mt-1 accent-[#8FA08B]" />
            <span className="text-sm text-stone-700">{t("portal.privacyAccept")}</span>
          </label>
          <button onClick={submit} disabled={saving || !form.privacy_consent_accepted} data-testid="portal-submit-form"
            className="w-full py-3 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? t("portal.saving") : t("portal.confirmAndSend")}
          </button>
        </div>

        <p className="text-xs text-stone-400 text-center">{t("portal.footer")}</p>
      </div>
    </div>
  );
}
